import { useState } from 'react';
import { Check, CreditCard, Lock } from 'lucide-react';
import Spinner from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import { WINGPOINT_TIER, TRUSTEE_TIER } from './pricingConfig';

// WingPoint-exclusive purchase section — only shown to WingPoint customers
// (user.is_wingpoint). Renders BOTH options Jeff requires:
//   (a) Trustee — $99/mo (legacy grandfathered $79/mo) · annual $948/yr
//   (b) WingPoint — $99/mo  (annual $1,188/yr)
//
// Each card carries its own Monthly/Annual toggle. Because the backend only
// supports the wingpoint plan as annual (PRICE_IDS has (\"wingpoint\",\"annual\")
// only; checkout returns 400 for wingpoint + non-annual), the WingPoint card
// always calls onSubscribe('wingpoint','annual') — the toggle is purely a
// presentation aid so the user can compare the monthly-equivalent ($99/mo)
// against the annual price ($1,188/yr). The Trustee card uses the real
// monthly and annual prices.
//
// Props:
//   onSubscribe     – (planId, period) => void
//   processing      – boolean
//   isTargetPlan    – boolean | string (gold ring for ?plan= deep-link; when a
//                     string, matches the tier id)
//   registerCardRef – (tierId) => ref callback (optional; registers both the
//                     trustee and wingpoint cards for auto-scroll)
//   userTrustCount  – number | undefined  (optional) count of trusts the
//                     user currently holds. If it exceeds tier.maxTrusts
//                     the card is grayed out and the button is disabled.

// ── Trustee option card ─────────────────────────────────────
function TrusteeOptionCard({ onSubscribe, processing, isTargetPlan, cardRef, userTrustCount }) {
  const [period, setPeriod] = useState('annual');
  const monthlyEquivalent = period === 'annual' ? (TRUSTEE_TIER.annual / 12).toFixed(2).replace(/\.00$/, '') : TRUSTEE_TIER.monthly;

// 2026-10-03 trust-count gate fix: backend rejects checkout when
// trust_count >= plan limit (Trustee=1). Frontend must match with >= so a
// WingPoint customer holding 1 trust sees this card locked instead of
// hitting a server 400 after clicking (Kevin Jemison incident).
const isIneligible = userTrustCount != null && TRUSTEE_TIER.maxTrusts !== Infinity && userTrustCount >= TRUSTEE_TIER.maxTrusts;

  return (
    <div
      ref={cardRef}
      className={`card-trust corner-mark relative border-border bg-card ${isIneligible ? 'opacity-50 pointer-events-none' : ''} ${isTargetPlan && !isIneligible ? 'ring-2 ring-gold ring-offset-2 ring-offset-subtle-bg' : ''}`}
      data-testid="plan-card-trustee"
    >
      {isIneligible ? (
        <div className="absolute top-0 right-0 bg-muted text-muted-foreground px-3 py-1 font-mono text-xs uppercase flex items-center gap-1">
          <Lock className="w-3 h-3" />
          Not Enough Trusts
        </div>
      ) : (
        <div className="absolute top-0 right-0 bg-navy text-white px-3 py-1 font-mono text-xs uppercase">
          1 Trust
        </div>
      )}
      <h3 className="font-serif text-xl text-navy mb-2">Trustee Plan</h3>
      <p className="text-xs font-mono text-muted-foreground mb-3">{TRUSTEE_TIER.trustLimit}</p>
      {isIneligible && (
        <p className="text-xs text-warning mb-3 font-medium">
          Your account has <span className="font-mono">{userTrustCount}</span> trust
          {userTrustCount !== 1 ? 's' : ''}. This plan supports up to{' '}
          {TRUSTEE_TIER.maxTrusts === Infinity ? 'unlimited' : TRUSTEE_TIER.maxTrusts}.
        </p>
      )}

      {/* Monthly/Annual toggle */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex items-center border border-border p-1">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setPeriod('monthly')}
            className={`text-xs font-medium ${period === 'monthly' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Monthly
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setPeriod('annual')}
            className={`text-xs font-medium ${period === 'annual' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Annual <span className="ml-1 text-success">Save 20%</span>
          </Button>
        </div>
      </div>

      <div className="flex items-baseline gap-1 mb-1">
        <span className="font-mono text-4xl text-navy">${monthlyEquivalent}</span>
        <span className="text-muted-foreground">/month</span>
      </div>
      {period === 'annual' ? (
        <>
          <p className="text-xs font-mono text-muted-foreground mb-2">
            ${TRUSTEE_TIER.annual.toLocaleString()} billed annually · save ${TRUSTEE_TIER.monthly * 12 - TRUSTEE_TIER.annual}/yr
          </p>
          <p className="text-xs text-success mb-3 font-medium">
            Save 20% with annual
          </p>
        </>
      ) : (
        <p className="text-xs font-mono text-muted-foreground mb-3">
          ${TRUSTEE_TIER.monthly * 12}/year · switch to annual to save ${TRUSTEE_TIER.monthly * 12 - TRUSTEE_TIER.annual}
        </p>
      )}

      <ul className="space-y-3 mb-6">
        {TRUSTEE_TIER.features.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-success flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={() => onSubscribe('trustee', period)}
        className="w-full btn-secondary"
        disabled={processing || isIneligible}
        data-testid="subscribe-trustee-btn"
      >
        {processing ? (
          <>
            <Spinner size={16} className="mr-2" />
            Processing...
          </>
        ) : isIneligible ? (
          <>
            <Lock className="w-4 h-4 mr-2" />
            Not Enough Trusts
          </>
        ) : (
          <>
            <CreditCard className="w-4 h-4 mr-2" />
            Subscribe to Trustee {period === 'annual' ? 'Annual' : 'Monthly'}
          </>
        )}
      </Button>
    </div>
  );
}

// ── WingPoint $119/mo or $99/mo option card ───────────────────
function WingPointOptionCard({ onSubscribe, processing, isTargetPlan, cardRef }) {
  const [period, setPeriod] = useState('annual');
  // Both monthly and annual are now real purchasable plans.
  // Monthly: $119/mo   Annual: $1,188/yr ($99/mo equivalent)
  const monthlyEquivalent = period === 'annual' ? (WINGPOINT_TIER.annual / 12).toFixed(2).replace(/\.00$/, '') : WINGPOINT_TIER.monthly;

  return (
    <div
      ref={cardRef}
      className={`card-trust corner-mark relative border-gold/40 bg-gold/5 ${isTargetPlan ? 'ring-2 ring-gold ring-offset-2 ring-offset-subtle-bg' : ''}`}
      data-testid={`plan-card-${WINGPOINT_TIER.id}`}
    >
      <div className="absolute top-0 right-0 bg-gold text-navy px-3 py-1 font-mono text-xs uppercase">
        WingPoint Exclusive
      </div>
      <h3 className="font-serif text-xl text-navy mb-2">WingPoint Plan</h3>
      <p className="text-xs font-mono text-muted-foreground mb-3">{WINGPOINT_TIER.trustLimit}</p>

      {/* Monthly/Annual toggle */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex items-center border border-border p-1">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setPeriod('monthly')}
            className={`text-xs font-medium ${period === 'monthly' ? 'btn-gold' : 'btn-secondary'}`}
          >
            Monthly
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setPeriod('annual')}
            className={`text-xs font-medium ${period === 'annual' ? 'btn-gold' : 'btn-secondary'}`}
          >
            Annual <span className="ml-1 text-success">save $240/yr</span>
          </Button>
        </div>
      </div>

      {/* Price display — always shows $/mo headline */}
      {period === 'monthly' ? (
        <>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-mono text-4xl text-navy">${monthlyEquivalent}</span>
            <span className="text-muted-foreground">/month</span>
            <span className="font-mono text-lg text-muted-foreground line-through ml-2">$149</span>
            <span className="text-xs text-muted-foreground">/mo</span>
          </div>
          <div className="inline-block bg-gold/10 text-gold px-2 py-1 font-mono text-xs font-semibold mb-2">
            Save $30/mo vs Estate $149/mo
          </div>
          <p className="text-xs text-muted-foreground mb-3 font-medium">
            ${WINGPOINT_TIER.monthly * 12}/year · switch to annual to save $240/yr
          </p>
        </>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-mono text-4xl text-navy">${monthlyEquivalent}</span>
            <span className="text-muted-foreground">/month</span>
            <span className="font-mono text-lg text-muted-foreground line-through ml-2">$149</span>
            <span className="text-xs text-muted-foreground">/mo</span>
          </div>
          <div className="inline-block bg-gold/10 text-gold px-2 py-1 font-mono text-xs font-semibold mb-2">
            Save $30/mo vs Estate $149/mo
          </div>
          <p className="text-xs font-mono text-success mb-3 font-medium">
            ${WINGPOINT_TIER.annual.toLocaleString()} billed annually · save $240/yr vs monthly
          </p>
        </>
      )}

      <ul className="space-y-3 mb-6">
        {WINGPOINT_TIER.features.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-success flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={() => onSubscribe('wingpoint', period)}
        className="w-full btn-primary"
        disabled={processing}
        data-testid="subscribe-wingpoint-btn"
      >
        {processing ? (
          <>
            <Spinner size={16} className="mr-2" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-4 h-4 mr-2" />
            Subscribe to WingPoint {period === 'annual' ? 'Annual' : 'Monthly'}
          </>
        )}
      </Button>
    </div>
  );
}

// ── Combined WingPoint purchase section ────────────────────────
// Replaces the previous single WingPointPlanCard. Renders the two options
// Jeff requires side by side so a WingPoint customer sees both a $79/mo
// (annual) Trustee plan and a $99 WingPoint plan, each with a
// monthly/annual billing toggle.
export default function WingPointPlanCard({
  onSubscribe,
  processing,
  isTargetPlan,
  registerCardRef,
  userTrustCount,
}) {
  return (
    <div className="mb-8" data-testid="wingpoint-purchase-section">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-serif text-xl text-navy">Choose Your WingPoint Plan</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        WingPoint customers get two exclusive options — both include annual billing savings.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TrusteeOptionCard
          onSubscribe={onSubscribe}
          processing={processing}
          isTargetPlan={isTargetPlan === true || isTargetPlan === 'trustee'}
          cardRef={registerCardRef ? registerCardRef('trustee') : undefined}
          userTrustCount={userTrustCount}
        />
        <WingPointOptionCard
          onSubscribe={onSubscribe}
          processing={processing}
          isTargetPlan={isTargetPlan === true || isTargetPlan === 'wingpoint'}
          cardRef={registerCardRef ? registerCardRef('wingpoint') : undefined}
        />
      </div>
    </div>
  );
}